import React from 'react';
import { format } from 'date-fns';
import { Mail, Phone, PlusCircle, CalendarClock } from 'lucide-react';
import { Lead } from '../data/mockLeads';
import { Badge } from './Badge';
import { Button } from './Button';
interface LeadRowProps {
  lead: Lead;
  onAction: (id: string, action: string) => void;
  showOwner?: boolean;
  canAssignCampaign?: boolean;
  availableCampaigns?: string[];
  onAssignCampaign?: (id: string, campaignName: string) => void;
}
export function LeadRow({
  lead,
  onAction,
  showOwner,
  canAssignCampaign,
  availableCampaigns = [],
  onAssignCampaign
}: LeadRowProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getCampaignBadge = () => <Badge variant="info" className="flex items-center gap-1" data-id="element-160" data-mp="3riuqbi00r9vk">
      <Mail size={12} data-id="element-161" data-mp="3mlm8xjmp39vd" /> Email Drip
    </Badge>;
  const getStatusBadge = () => {
    switch (lead.status) {
      case 'pending':
        return <Badge variant="warning" data-id="element-162" data-mp="2pfx2kazvve49">Pending</Badge>;
      case 'contacted':
        return <Badge variant="success" data-id="element-163" data-mp="2uegsemhlla1w">Contacted</Badge>;
      case 'snoozed':
        return <Badge variant="default" data-id="element-164" data-mp="2ze36ey592ubr">Snoozed</Badge>;
    }
  };
  return <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors group" data-id="element-165" data-mp="2zcs0oss8vci0">
      <td className="py-4 px-6" data-id="element-166" data-mp="33e3tf76f0duf">
        <div className="flex flex-col" data-id="element-167" data-mp="26q7avwrf38gk">
          <span className="font-semibold text-slate-900" data-id="element-168" data-mp="26r8p2a3qcb2i">{lead.name}</span>
          <span className="text-sm text-slate-500" data-id="element-169" data-mp="2bo8uqjwo3bt3">{lead.email}</span>
          <span className="text-sm text-slate-500" data-id="element-170" data-mp="2fqncf0olo5is">{lead.phone}</span>
        </div>
      </td>
      {showOwner && <td className="py-4 px-6" data-id="element-171" data-mp="2khvn001o1wxj">
          <div className="flex items-center gap-2" data-id="element-172" data-mp="2kiwww16y4ddo">
            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 uppercase" data-id="element-173" data-mp="1nzpijtahyevq">
              {lead.ownerName.substring(0, 2)}
            </div>
            <span className="text-sm text-slate-700 font-medium" data-id="element-174" data-mp="2vbf5stgkgzrg">
              {lead.ownerName}
            </span>
          </div>
        </td>}
      <td className="py-4 px-6" data-id="element-175" data-mp="2qclth4d0nesv">
        <div className="flex flex-col" data-id="element-176" data-mp="351rxr6qpnv7m">
          <span className="font-medium text-slate-900" data-id="element-177" data-mp="350glsjn43h5f">
            {formatCurrency(lead.loanAmount)}
          </span>
          <span className="text-sm text-slate-500" data-id="element-178" data-mp="303gr14vpq0tm">{lead.loanPurpose}</span>
        </div>
      </td>
      <td className="py-4 px-6" data-id="element-179" data-mp="2cdcol0u9g747">
        {lead.campaignName ? <div className="flex flex-col gap-1" data-id="element-180" data-mp="27esx6kwwe34g">
            <span className="text-sm text-slate-900 font-medium truncate max-w-[180px]" title={lead.campaignName} data-id="element-181" data-mp="27g41clu99cw3">
              {lead.campaignName}
            </span>
            <div data-id="element-182" data-mp="2hejsupx72o64">{getCampaignBadge()}</div>
          </div> : canAssignCampaign && onAssignCampaign ? <div className="flex flex-col gap-1.5 max-w-[200px]" data-id="element-183" data-mp="3srmc5sqohrtz">
            <span className="text-sm text-slate-400 italic" data-id="element-184" data-mp="3sr2qq21tklb7">
              Not in a campaign
            </span>
            <div className="relative" data-id="element-185" data-mp="47u0irf91a10">
              <PlusCircle size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-600 pointer-events-none" data-id="element-186" data-mp="94u6l2c3mcs4" />
              <select aria-label={`Add ${lead.name} to a campaign`} value="" onChange={e => {
            if (e.target.value) onAssignCampaign(lead.id, e.target.value);
          }} className="w-full pl-8 pr-3 py-1.5 border border-blue-200 bg-blue-50 text-blue-700 rounded-md text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer hover:bg-blue-100 transition-colors" data-id="element-187" data-mp="3vbsig72rludp">
                <option value="" data-id="element-188" data-mp="15ub9j9jp5kq4">Add to campaign…</option>
                {availableCampaigns.map(name => <option key={name} value={name} data-id="element-189" data-mp="1altahxlpzlb3">
                    {name}
                  </option>)}
              </select>
            </div>
          </div> : <span className="text-sm text-slate-400 italic" data-id="element-190" data-mp="3e2zv6ui4a86z">
            Not in a campaign
          </span>}
      </td>
      <td className="py-4 px-6" data-id="element-191" data-mp="3957tj8ro77pl">
        <div className="flex flex-col" data-id="element-192" data-mp="396iyvd0rknit">
          <span className="text-sm text-slate-900" data-id="element-193" data-mp="345vakz3x6ksa">
            {format(new Date(lead.lastContact), 'MMM d, yyyy')}
          </span>
          <span className="text-xs text-slate-500" data-id="element-194" data-mp="5n6g8k3fp4s1">
            {format(new Date(lead.lastContact), 'h:mm a')}
          </span>
        </div>
      </td>
      <td className="py-4 px-6" data-id="element-195" data-mp="3s30yeglutrpm">
        {lead.nextFollowUp ? <div className="flex items-start gap-1.5" data-id="element-196" data-mp="3s17p0tjuwtdx">
            <CalendarClock size={14} className="text-emerald-600 mt-0.5 shrink-0" data-id="element-197" data-mp="3n4zddq4vbibi" />
            <div className="flex flex-col" data-id="element-198" data-mp="ofs7ss7b696t">
              <span className="text-sm text-slate-900" data-id="element-199" data-mp="jnh4dfdd059i">
                {format(new Date(lead.nextFollowUp), 'MMM d, yyyy')}
              </span>
              <span className="text-xs text-slate-500" data-id="element-200" data-mp="rxg6n6qr65ou">
                {format(new Date(lead.nextFollowUp), 'h:mm a')}
              </span>
            </div>
          </div> : <span className="text-sm text-slate-400 italic" data-id="element-201" data-mp="10vrvb3826ten">Not scheduled</span>}
      </td>
      <td className="py-4 px-6" data-id="element-202" data-mp="15wxpl5hdaoqa">{getStatusBadge()}</td>
      <td className="py-4 px-6 text-right" data-id="element-203" data-mp="15tjp62s9kplh">
        <div className="flex items-center justify-end gap-2" data-id="element-204" data-mp="1atxxllxs21k1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full" onClick={() => onAction(lead.id, 'call')} title="Log Call" data-id="element-205" data-mp="1ewkrrthx7yke">
            <Phone size={16} data-id="element-206" data-mp="1omxadg66dhnw" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full" onClick={() => onAction(lead.id, 'email')} title="Send Email" data-id="element-207" data-mp="1olm7rjogufyd">
            <Mail size={16} data-id="element-208" data-mp="1yih1lp9sns6b" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full" onClick={() => onAction(lead.id, 'schedule')} title="Set Next Follow-up" data-id="element-209" data-mp="18vmvb73vebr1">
            <CalendarClock size={16} data-id="element-210" data-mp="1insh1z3j3wxs" />
          </Button>
        </div>
      </td>
    </tr>;
}